import React from 'react';
import { translate } from 'translations/i18n';
import { NetworkStatus } from '@apollo/client';
import ProjectTasksList from 'components/projects/legacyProjectTasks/ProjectTasksList';
import useCreateProjectTaskFromPhoto from 'components/projects/legacyProjectTasks/hooks/useCreateProjectTaskFromPhoto';
import useCreateProjectTaskFromVideo from 'components/projects/legacyProjectTasks/hooks/useCreateProjectTaskFromVideo';
import useGetAssetProjectTasks from 'components/projects/legacyProjectTasks/hooks/useGetAssetProjectTasks';
import styled from 'styled-components';
import { AssetModalSidebarHeader } from './shared/styles';
import { Notice } from '@companycam/slab-web';

const AssetModalTasksContainer = styled.div`
  margin: var(--cc_size_spacing_m);
  margin-top: 0;
  background: var(--cc_color_background_2);
  border-radius: var(--cc_size_border_radius_l);
  padding: var(--cc_size_spacing_m);
  // needed to offset the top margin on the TaskHeader (16 + 10)
  padding-top: 26px;
`;

const TasksHeader = styled.div`
  // needed to position the CCTooltip popover correctly
  margin-top: -10px;
  margin-bottom: var(--cc_size_spacing_xs);
  font-size: var(--cc_size_text_m);
  font-family: 'Averta', system-ui, sans-serif;
  font-weight: 600;
`;

const ErrorLabel = styled.span`
  font-family: system-ui, sans-serif;
  font-weight: 400;
  font-size: var(--cc_size_text_xs);
  color: var(--cc_color_text_nonessential);
  margin-top: var(--cc_size_spacing_m);
  margin-left: var(--cc_size_spacing_l);
`;

type LegacyProjectTasksProps = {
  assetId: string;
  assetType: string;
  projectId: string;
};

const LegacyProjectTasks = ({
  assetId,
  assetType,
  projectId,
}: LegacyProjectTasksProps) => {
  const {
    incompleteTasks,
    completedTasks,
    addNewTask,
    addNewTaskAtEnd,
    removeNewTask,
    loading,
    networkStatus,
    error,
  } = useGetAssetProjectTasks({
    assetId,
    projectId,
  });

  const { createProjectTaskFromPhoto } = useCreateProjectTaskFromPhoto({
    photoId: assetId,
    projectId,
  });

  const { createProjectTaskFromVideo } = useCreateProjectTaskFromVideo({
    videoId: assetId,
    projectId,
  });

  const isPhoto = assetType === 'Photo';
  const isVideo = assetType === 'Video';

  const isVideoOrPhoto = isPhoto || isVideo;

  if (error) {
    return (
      <>
        <AssetModalSidebarHeader>
          {translate('projects.show.projectSidebar.tasks')}
        </AssetModalSidebarHeader>
        <ErrorLabel>
          {translate('photoProjectTasks.getErrorMessage')}
        </ErrorLabel>
      </>
    );
  }

  const isLoading = loading && networkStatus === NetworkStatus.loading;

  return (
    <AssetModalTasksContainer>
      <TasksHeader data-testid="asset__tasks-header">
        {translate('projects.show.projectSidebar.tasks')}
      </TasksHeader>
      {isVideoOrPhoto ? (
        <ProjectTasksList
          loading={isLoading}
          incompleteTasks={incompleteTasks}
          completedTasks={completedTasks}
          createProjectTask={
            isPhoto ? createProjectTaskFromPhoto : createProjectTaskFromVideo
          }
          removeNewTask={removeNewTask}
          addNewTask={addNewTask}
          addNewTaskAtEnd={addNewTaskAtEnd}
          isPhotoView
          assetId={assetId}
        />
      ) : (
        <Notice style={{ background: 'transparent', padding: 0 }}>
          {translate('photoProjectTasks.videoNotSupportedNotice')}
        </Notice>
      )}
    </AssetModalTasksContainer>
  );
};

export default LegacyProjectTasks;
